/**
 * Moving training days.
 *
 * Model: a per-day override, `sched:<day>`, holding a session id or 'rest'. Absence
 * means the day's scheduled session, consistent with the rest of the key space.
 *
 * Why an override rather than re-dating the plan: calendar weeks stay intact, so gates,
 * de-loads, hold logic and the trailing 7-day means are all untouched. Ticks stay keyed
 * by (day, session), so an override never destroys work — undo it and the ticks
 * reappear exactly where they were.
 *
 * What this deliberately does NOT move: meals. The post-training slot is a fixed
 * calendar property (Mon, Tue, Thu, Sat) and is NOT derivable from session type —
 * Friday's run has no post slot in the data. Making meals follow training would mean
 * inventing a rule the plan does not contain.
 */
import { PLAN } from './data.ts';
import type { PlanState } from './types.ts';
import { sessionIdForDow, kindOf } from './session.ts';
import type { SessionId } from './session.ts';
import { isDeload } from './phase.ts';
import { MAX_DAY_INDEX } from './dates.ts';

export type ScheduleEntry = SessionId | 'rest';

export const scheduleKey = (day: number): string => `sched:${day}`;

const VALID: readonly ScheduleEntry[] =
  ['liftA', 'liftB', 'run0', 'run1', 'run2', 'recWed', 'recSun', 'rest'] as const;

export function isScheduleEntry(v: unknown): v is ScheduleEntry {
  return typeof v === 'string' && (VALID as readonly string[]).includes(v);
}

/** What actually happens on a day: the override if there is one, else the plan. */
export function effectiveSessionId(state: PlanState, dayIndex: number): ScheduleEntry {
  const v = state.get<unknown>(scheduleKey(dayIndex));
  return isScheduleEntry(v) ? v : sessionIdForDow(dayIndex % 7);
}

export function isOverridden(state: PlanState, dayIndex: number): boolean {
  return isScheduleEntry(state.get<unknown>(scheduleKey(dayIndex)));
}

export const weekOf = (day: number) => Math.floor(day / 7) + 1;
export const weekStartDay = (week: number) => (week - 1) * 7;
export const weekEndDay = (week: number) => Math.min((week - 1) * 7 + 6, MAX_DAY_INDEX);

export interface ShiftStep {
  day: number;
  from: ScheduleEntry;
  to: ScheduleEntry;
}

export interface ShiftPreview {
  /** The day you are picking up ON — the day you are looking at. */
  fromDay: number;
  /** The day whose session you missed. Any earlier day, not just yesterday. */
  missedDay: number;
  missedSession: ScheduleEntry;
  /** How many days the remainder slides by. */
  shiftBy: number;
  week: number;
  steps: ShiftStep[];
  /** Pushed off the end of the week. There is only room for seven days. */
  dropped: ScheduleEntry[];
  writes: Array<{ key: string; value: ScheduleEntry }>;
  warnings: string[];
  blocked: string | null;
}

/** Sessions you could still pick up, most recent first. */
export interface MissedCandidate {
  day: number;
  session: SessionId;
}

export const MAX_LOOKBACK_DAYS = 6;

/**
 * Days before `targetDay` that still hold a real session — the things you could be
 * picking up. Excludes days already marked rest, since those are already accounted for.
 */
export function missedCandidates(
  state: PlanState, targetDay: number, lookback = MAX_LOOKBACK_DAYS,
): MissedCandidate[] {
  const out: MissedCandidate[] = [];
  for (let d = targetDay - 1; d >= Math.max(0, targetDay - lookback); d--) {
    const s = effectiveSessionId(state, d);
    if (s !== 'rest') out.push({ day: d, session: s });
  }
  return out;
}

/**
 * Shift the rest of the week so `fromDay` picks up the session you missed on
 * `missedDay`, and everything after slides along by the same number of days.
 *
 * The shift is uniform, which is what preserves relative spacing — it is why Lift A
 * stays the same number of days clear of the key run. Days between the miss and the
 * pick-up are recorded as rest, because they did not happen either.
 */
export function planShift(
  state: PlanState, fromDay: number, missedDayArg?: number,
): ShiftPreview {
  const week = weekOf(fromDay);
  const endDay = weekEndDay(week);
  const missedDay = missedDayArg ?? fromDay - 1;
  const shiftBy = fromDay - missedDay;
  const missedSession: ScheduleEntry = missedDay >= 0
    ? effectiveSessionId(state, missedDay) : 'rest';

  const base: ShiftPreview = {
    fromDay, missedDay, missedSession, shiftBy, week,
    steps: [], dropped: [], writes: [], warnings: [], blocked: null,
  };

  if (missedDay < 0) {
    return { ...base, blocked: 'That is before the plan starts.' };
  }
  if (shiftBy < 1) {
    return { ...base, blocked: 'You can only pick up a session from an earlier day.' };
  }
  if (missedSession === 'rest') {
    return { ...base, blocked: 'That day is already marked as rest, so there is nothing to pick up.' };
  }
  if (fromDay > endDay) return { ...base, blocked: 'Outside the plan.' };

  // Every source value is read from the ORIGINAL state, so this is one clean uniform
  // shift rather than a cascade of overwrites.
  const steps: ShiftStep[] = [];
  const writes: Array<{ key: string; value: ScheduleEntry }> = [];

  const dropped: ScheduleEntry[] = [];
  for (let d = Math.max(endDay - shiftBy + 1, fromDay); d <= endDay; d++) {
    const s = effectiveSessionId(state, d);
    if (s !== 'rest') dropped.push(s);
  }

  // Everything you missed, and everything you skipped getting here, is rest.
  for (let d = missedDay; d < fromDay; d++) {
    if (effectiveSessionId(state, d) === 'rest') continue;
    steps.push({ day: d, from: effectiveSessionId(state, d), to: 'rest' });
    writes.push({ key: scheduleKey(d), value: 'rest' });
  }
  for (let d = fromDay; d <= endDay; d++) {
    const from = effectiveSessionId(state, d);
    const to = effectiveSessionId(state, d - shiftBy);
    steps.push({ day: d, from, to });
    writes.push({ key: scheduleKey(d), value: to });
  }

  const after = new Map<number, ScheduleEntry>(steps.map((s) => [s.day, s.to]));
  const effAfter = (d: number): ScheduleEntry => after.get(d) ?? effectiveSessionId(state, d);

  const warnings = warningsFor(effAfter, week, dropped);
  if (shiftBy > 3) {
    warnings.push(`You are picking up a session from ${shiftBy} days ago. Rule 7: miss a week, `
      + `repeat that week rather than skipping ahead — a hold may suit this better than a shift.`);
  }
  return { ...base, steps, dropped, writes, warnings };
}

/** Checks the section 3 spacing rules and the plan's own rules 7 and 8. */
function warningsFor(
  eff: (d: number) => ScheduleEntry, week: number, dropped: readonly ScheduleEntry[],
): string[] {
  const out: string[] = [];
  const start = weekStartDay(week), end = weekEndDay(week);
  const days: number[] = [];
  for (let d = start; d <= end; d++) days.push(d);
  const at = new Map<ScheduleEntry, number[]>();
  for (const d of days) {
    const s = eff(d);
    at.set(s, [...(at.get(s) ?? []), d]);
  }

  for (const d of dropped) {
    if (d !== 'rest') out.push(`${LABELS[d]} falls off the end of the week and will not happen.`);
  }

  // Rule 7: "never double up". Also catches a session duplicated in a week, which a
  // hand-edited schedule can produce and which would double that week's volume.
  for (const [s, ds] of at) {
    if (s === 'rest' || ds.length < 2) continue;
    const kind = kindOfEntry(s);
    if (kind === 'run') {
      out.push(`${LABELS[s]} appears ${ds.length} times this week. Rule 7: never double up.`);
    } else if (kind === 'lift') {
      out.push(`${LABELS[s]} appears ${ds.length} times this week, which doubles that `
        + `session's volume. The plan is two full-body sessions a week.`);
    }
  }

  // Section 3: Lift A is Monday precisely to sit five days clear of the key Saturday run.
  // Check EVERY pairing and report the tightest, not just the first one found.
  const liftDays = at.get('liftA') ?? [];
  const keyRunDays = at.get('run2') ?? [];
  let tightest: number | null = null;
  for (const l of liftDays) {
    for (const k of keyRunDays) {
      const gap = k - l;
      if (gap > 0 && (tightest === null || gap < tightest)) tightest = gap;
    }
  }
  if (tightest !== null && tightest < 5) {
    out.push(`Heavy lower-body work would sit ${tightest} day${tightest === 1 ? '' : 's'} `
      + `before the key run, rather than the five the plan is built around.`);
  }

  // Rule 8: the level test sits at the end of the week so it is taken fresh.
  if (isDeload(week)) {
    const keyRun = keyRunDays.length ? keyRunDays[keyRunDays.length - 1] : undefined;
    if (keyRun === undefined) {
      out.push(`Week ${week} ends in a level test and this shift removes it from the week. `
        + `The checkpoint cannot be completed without it.`);
    } else {
      const dayBefore = eff(keyRun - 1);
      if (dayBefore === 'liftA' || dayBefore === 'liftB') {
        out.push(`A lifting session lands the day before the level test. Rule 8: a test taken `
          + `tired measures your fatigue, not your fitness.`);
      }
    }
  }
  return out;
}

function kindOfEntry(s: ScheduleEntry): 'lift' | 'run' | 'recovery' | 'rest' {
  return s === 'rest' ? 'rest' : kindOf(s);
}

export const LABELS: Record<ScheduleEntry, string> = {
  liftA: 'Lift A', liftB: 'Lift B',
  run0: 'Run 1 (easy)', run1: 'Run 2 (short easy)', run2: 'Run 3 (long/key)',
  recWed: 'Midweek recovery', recSun: 'Sunday recovery, weigh and shop',
  rest: 'Rest (missed)',
};

/** Keys to delete to put a week back on plan. */
export function weekResetKeys(week: number): string[] {
  const out: string[] = [];
  for (let d = weekStartDay(week); d <= weekEndDay(week); d++) out.push(scheduleKey(d));
  return out;
}

export function weekHasOverrides(state: PlanState, week: number): boolean {
  for (let d = weekStartDay(week); d <= weekEndDay(week); d++) if (isOverridden(state, d)) return true;
  return false;
}

/** Standing warnings for a week as it currently stands, however it got that way. */
export function scheduleWarnings(state: PlanState, week: number): string[] {
  if (!weekHasOverrides(state, week)) return [];
  return warningsFor((d) => effectiveSessionId(state, d), week, []);
}

export { PLAN };
